import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  eachHourOfInterval,
  eachWeekOfInterval,
  format,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
  subDays,
} from "date-fns";
import type {
  CallStatsGranularity,
  CallStatsTrendPoint,
} from "@/services/callStatsService";

export type RangePreset =
  | "today"
  | "7d"
  | "30d"
  | "90d"
  | "month"
  | "1y"
  | "all"
  | "custom";

export const DEFAULT_PRESET: RangePreset = "7d";

const PRESETS: RangePreset[] = [
  "today",
  "7d",
  "30d",
  "90d",
  "month",
  "1y",
  "all",
  "custom",
];
const PRESET_DAYS: Record<string, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "1y": 365,
};
// "All" asks for everything since a date earlier than any call.
const ALL_TIME_START = new Date(2020, 0, 1);
const DATE_PARAM = "yyyy-MM-dd";

export interface RangeSelection {
  preset: RangePreset;
  customFrom?: Date;
  customTo?: Date;
}

export interface ResolvedRange {
  from: Date;
  to: Date;
  prevFrom: Date;
  prevTo: Date;
  days: number;
}

const HALF_HOUR_MS = 30 * 60 * 1000;

// Stats are stored in 30-minute buckets, so "now" is rounded up to the next one.
function endOfCurrentBucket(now: Date): Date {
  return new Date(Math.ceil(now.getTime() / HALF_HOUR_MS) * HALF_HOUR_MS);
}

function rangeStart(
  preset: RangePreset,
  customFrom: Date | undefined,
  now: Date,
): Date {
  if (preset === "today") return startOfDay(now);
  if (preset === "month") return startOfMonth(now);
  if (preset === "all") return ALL_TIME_START;
  if (preset === "custom" && customFrom) return startOfDay(customFrom);
  return startOfDay(subDays(now, (PRESET_DAYS[preset] ?? 7) - 1));
}

// `to` is exclusive and never in the future. The previous period is the same
// span shifted back by whole days, so a range that ends "now" is compared with
// the same hours of the earlier days, not with full days.
export function resolveRange(
  preset: RangePreset,
  customFrom?: Date,
  customTo?: Date,
  now: Date = new Date(),
): ResolvedRange {
  const from = rangeStart(preset, customFrom, now);
  const lastDay = preset === "custom" && customTo ? customTo : now;
  const endOfLastDay = startOfDay(addDays(lastDay, 1));
  const bucketEnd = endOfCurrentBucket(now);
  const to = endOfLastDay < bucketEnd ? endOfLastDay : bucketEnd;
  const days = differenceInCalendarDays(new Date(to.getTime() - 1), from) + 1;
  if (preset === "all") {
    // Nothing comes before all time: the previous period is one empty day.
    return { from, to, prevFrom: subDays(from, 1), prevTo: from, days };
  }
  return {
    from,
    to,
    prevFrom: subDays(from, days),
    prevTo: subDays(to, days),
    days,
  };
}

function parseDateParam(value: string | null): Date | undefined {
  if (!value) return undefined;
  const date = parse(value, DATE_PARAM, new Date());
  return isValid(date) ? date : undefined;
}

export function parseRangeParams(searchParams: URLSearchParams): RangeSelection {
  const raw = searchParams.get("range") as RangePreset | null;
  if (!raw || !PRESETS.includes(raw)) return { preset: DEFAULT_PRESET };
  if (raw !== "custom") return { preset: raw };

  let customFrom = parseDateParam(searchParams.get("from"));
  let customTo = parseDateParam(searchParams.get("to"));
  if (!customFrom || !customTo) return { preset: DEFAULT_PRESET };
  if (customFrom > customTo) [customFrom, customTo] = [customTo, customFrom];
  return { preset: "custom", customFrom, customTo };
}

export function rangeToParams(selection: RangeSelection): URLSearchParams {
  const params = new URLSearchParams();
  if (selection.preset === DEFAULT_PRESET) return params;
  params.set("range", selection.preset);
  if (selection.preset === "custom" && selection.customFrom && selection.customTo) {
    params.set("from", format(selection.customFrom, DATE_PARAM));
    params.set("to", format(selection.customTo, DATE_PARAM));
  }
  return params;
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function previousPeriodLabel(preset: RangePreset, days: number): string {
  if (preset === "today") return "vs yesterday, same hours";
  if (preset === "all") return "no earlier period to compare with";
  return days === 1 ? "vs the day before" : `vs previous ${days} days`;
}

export function formatRangeLabel(
  preset: RangePreset,
  from: Date,
  to: Date,
  tz: string,
): string {
  if (preset === "all") return `All time · ${tz}`;
  const lastDay = new Date(to.getTime() - 1);
  const sameYear = from.getFullYear() === lastDay.getFullYear();
  return `${format(from, sameYear ? "MMM d" : "MMM d, yyyy")} – ${format(lastDay, "MMM d, yyyy")} · ${tz}`;
}

const BUCKET_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

export function fillTrendBuckets(
  trend: CallStatsTrendPoint[],
  from: Date,
  to: Date,
  granularity: CallStatsGranularity,
): CallStatsTrendPoint[] {
  const interval = { start: from, end: new Date(to.getTime() - 1) };
  let starts: Date[];
  if (granularity === "hour") starts = eachHourOfInterval(interval);
  else if (granularity === "day") starts = eachDayOfInterval(interval);
  else starts = eachWeekOfInterval(interval, { weekStartsOn: 1 });

  const byBucket = new Map(trend.map((point) => [point.bucket.slice(0, 19), point]));
  return starts.map((start) => {
    const bucket = format(start, BUCKET_FORMAT);
    return (
      byBucket.get(bucket) ?? {
        bucket,
        calls: 0,
        connected: 0,
        seconds: 0,
        cost_total: 0,
      }
    );
  });
}
